import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

/**
 * Hook for updating the allowUnsubscribed property of an automation template.
 *
 * This mutation enables/disables the ability to send automation communications
 * to unsubscribed contacts. This should only be used for critical transactional
 * communications to stay in compliance with CAN-SPAM regulations.
 *
 * @param options - Configuration options for the mutation
 * @param options.onSuccess - Callback fired when the mutation succeeds
 * @param options.onError - Callback fired when the mutation fails
 * @returns A React Query mutation object
 */
export function useAutomationTemplateAllowUnsubscribedMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IAutomationTemplate) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({
			id,
			allowUnsubscribed,
			impersonationContext,
		}: {
			id: string;
			allowUnsubscribed: boolean;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			return userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `automationTemplate/${encodeURIComponent(id)}/allowUnsubscribed`,
					queryParams: {
						allowUnsubscribed,
					},
					impersonationContext,
				}),
				'PUT'
			);
		},
		onSuccess,
		onError,
	});
}
